import { createContext, useContext, useState, useEffect, useCallback } from 'react';
import api from './api';

// Special sentinel — means "assigned to every portal user"
export const ASSIGN_ALL = 'All';

// ─── Context ──────────────────────────────────────────────────────────────────

const TasksContext = createContext(null);

const fromApi = t => ({
  id:          t.id,
  projectId:   t.projectId  || null,
  projectName: t.projectName || '',
  title:       t.title,
  description: t.description || '',
  status:      t.status,
  priority:    t.priority || 'Medium',
  // assignedTo зберігає ID користувача, або сентінел ASSIGN_ALL
  assignedTo:     t.assignAll ? ASSIGN_ALL : (t.assignedTo || null),
  assignedToName: t.assignAll ? 'All Users' : (t.assignedToName || ''),
  deadline:    t.deadline || '',
  createdBy:     t.createdBy || null,
  createdByName: t.createdByName || '',
  createdAt:   t.createdAt,
});

const toApiPayload = form => {
  const out = { ...form };
  if (out.assignedTo !== undefined) {
    out.assignAll  = out.assignedTo === ASSIGN_ALL;
    out.assignedTo = out.assignedTo === ASSIGN_ALL ? null : out.assignedTo;
  }
  return out;
};

// A task is "visible" to a user if assignedTo === user.id OR assignedTo === ASSIGN_ALL
const visibleTo = (task, userId) =>
  task.assignedTo === userId || task.assignedTo === ASSIGN_ALL;

export function TasksProvider({ children, currentUser = null }) {
  const [tasks, setTasks]     = useState([]);
  const [loading, setLoading] = useState(false);

  const reload = useCallback(() => {
    setLoading(true);
    return api.tasks.list()
      .then(res => setTasks((res.tasks || []).map(fromApi)))
      .catch(() => {})
      .finally(() => setLoading(false));
  }, []);

  useEffect(() => { reload(); }, [reload]);

  // ── CRUD ──

  const addTask = async task => {
    const { task: created } = await api.tasks.create(toApiPayload(task));
    const t = fromApi(created);
    setTasks(prev => [...prev, t]);
    return t;
  };

  const updateTask = async (id, changes) => {
    const { task: updated } = await api.tasks.update(id, toApiPayload(changes));
    const t = fromApi(updated);
    setTasks(prev => prev.map(x => x.id === id ? t : x));
    return t;
  };

  const deleteTask = async id => {
    await api.tasks.delete(id);
    setTasks(prev => prev.filter(t => t.id !== id));
  };

  // ── Queries ──

  const myId = currentUser?.id;

  const getAllTasks = () => tasks;

  const getMyTasks = () =>
    tasks.filter(t => visibleTo(t, myId));

  const getProjectTasks = projectId =>
    tasks.filter(t => t.projectId === projectId);

  const getPendingTasks = () =>
    tasks.filter(t => visibleTo(t, myId) && t.status !== 'Done');

  const getOverdueTasks = () => {
    const today = new Date().toISOString().slice(0, 10);
    return tasks.filter(
      t => visibleTo(t, myId) && t.status !== 'Done' && t.deadline && t.deadline < today
    );
  };

  const getDueSoonTasks = () => {
    const today = new Date().toISOString().slice(0, 10);
    const limit = new Date();
    limit.setDate(limit.getDate() + 3);
    const limitStr = limit.toISOString().slice(0, 10);
    return tasks.filter(
      t => visibleTo(t, myId) && t.status !== 'Done' && t.deadline >= today && t.deadline <= limitStr
    );
  };

  return (
    <TasksContext.Provider value={{
      tasks, currentUser, loading, reload,
      addTask, updateTask, deleteTask,
      getAllTasks, getMyTasks, getProjectTasks,
      getPendingTasks, getOverdueTasks, getDueSoonTasks,
    }}>
      {children}
    </TasksContext.Provider>
  );
}

export function useTasks() {
  const ctx = useContext(TasksContext);
  if (!ctx) throw new Error('useTasks must be used inside <TasksProvider>');
  return ctx;
}
